import { Injectable } from '@angular/core';
import { Subject, BehaviorSubject, combineLatest } from 'rxjs';
import { BuildingTemplates, BuildingTemplate } from './building-template.interface';
import { ApiService } from '../../api/api.service';
import { take } from 'rxjs/operators';
import { ProjectModule } from '../module/project-module.interface';
import { ProjectElement } from '../element/project-element.interface';
import { TemplateService } from '../../utilities/template/template.service';
import { ApiLegacyService } from '../../api/legacy/api-legacy.service';

@Injectable({
  providedIn: 'root'
})
export class BuildingTemplateCoreService {

  /** Building templates. */
  protected _buildingTemplates: Subject<BuildingTemplates> = new BehaviorSubject<BuildingTemplates>(null);

  /** Building templates observable. */
  public buildingTemplate$ = this._buildingTemplates.asObservable();

  /** Building templates elements. */
  protected _buildingTemplateElements: Subject<ProjectElement[]> = new BehaviorSubject<ProjectElement[]>(null);

  /** Building templates elements observable. */
  public buildingTemplateElement$ = this._buildingTemplateElements.asObservable();

  /** Building templates modules. */
  protected _buildingTemplateModules: Subject<ProjectModule[]> = new BehaviorSubject<ProjectModule[]>(null);

  /** Building templates modules observable. */
  public buildingTemplateModule$ = this._buildingTemplateModules.asObservable();

  constructor(
    protected apiService: ApiService,
    protected apiLegacyService: ApiLegacyService,
    protected templateService: TemplateService
  ) { }

  /**
   * Get templates.
   *
   * @returns Building templates.
   */
  public get(): BuildingTemplates {
    let templates: BuildingTemplates;
    this._buildingTemplates.pipe(take(1)).subscribe(t => templates = t);
    return templates;
  }

  /**
   * Get templates.
   *
   * @param id Template id.
   * @returns Building template.
   */
  public getTemplate(id: BuildingTemplate['_id']): BuildingTemplate {
    let template: BuildingTemplate;
    this._buildingTemplates.pipe(take(1)).subscribe(ts => template = ts.find(t => t._id === id));
    return template;
  }

  /**
   * Get template elements.
   *
   * @returns Building elements.
   */
  public getElements(): ProjectElement[] {
    let elements: ProjectElement[];
    this._buildingTemplateElements.pipe(take(1)).subscribe(e => elements = e);
    return elements;
  }

  /**
   * Get template modules.
   *
   * @returns Building modules.
   */
  public getModules(): ProjectModule[] {
    let modules: ProjectModule[];
    this._buildingTemplateModules.pipe(take(1)).subscribe(m => modules = m);
    return modules;
  }

  /**
   * Build template tree with elements.
   *
   * By default, the template does only contain an *array of element id's*,
   * use this method to populate the modules with the correct elements.
   *
   * @param id Template id to build.
   * @returns Template with correct elements.
   */
  public getTemplateTree(id: BuildingTemplate['_id']): ProjectModule[] {
    return this.templateService.populateTreeWithElements(
      this.getTemplate(id),
      this.getModules(),
      this.getElements()
    );
  }

  /**
   * Initialize templates.
   *
   * Resolver function with legacy support for the AngularJS router.
   * For the best results, and easy migration later on, the data is
   * requested from the API, saved here in the datastore, and a fake
   * Promise response is returned to the legacy router. Because of
   * this, the legacy router will wait for the datastore to complete.
   *
   * The API is only called *once*. When the data is already available
   * locally, the cached templates are returned.

   * @note Because it will return only cached data, there may be old
   *  data after updating the templates via the template routes. This
   *  store does not sync the data, because the legacy template pages
   *  still only use $scope. If so, add a clear() function which will
   *  be called when switching between project and template pages.
   *
   * @todo After migration, remove the Promise, and move this method
   *  to proper Angular Router Resolver.
   *
   * @returns Promise when the data is available. Empty response.
   */
  public resolve(): Promise<boolean> {
    return new Promise(resolve => {
      const templates = this.get();
      if (templates) {
        resolve();
      } else {
        combineLatest([
          this.apiLegacyService.getBuildingTemplates(),
          this.apiLegacyService.getTemplateModules(),
          this.apiLegacyService.getTemplateElements(2)
        ]).subscribe(([buildingTemplates, modules, elements]: [BuildingTemplates, ProjectModule[], ProjectElement[]]) => {
          this._buildingTemplates.next(buildingTemplates);
          this._buildingTemplateModules.next(modules);
          this._buildingTemplateElements.next(elements);
          resolve(true);
        });
      }
    });
  }
}
